"use strict";

/**
 * The only sanctioned path for worksheet HTML into a Vue template.
 *
 * Trust boundary (Phase 4.5 task 2, finding P2.1): a worksheet entry's
 * `additional_info` is worksheet-service content, not application content.
 * Worksheet rows are authored broadly through the worksheet service rather
 * than in this repository, the field legitimately carries rich formatting
 * that coders read while choosing an intervention, and both pickers render it
 * through `v-html`. It can therefore be neither trusted (an `onerror`
 * payload on an inserted element would be stored XSS, and selected entry
 * objects persist in encounters) nor flattened to text (that would delete
 * real coding guidance). The resolution is an explicit allowlist applied on
 * every render, and this module is the only place allowed to produce a string
 * for `v-html`. Any other surface that needs the field must call
 * sanitizeWorksheetHtml(), or escapeWorksheetHtml() when it wants text.
 *
 * The module fails closed: when no usable purifier can be resolved, callers
 * get the HTML-escaped source, never the raw markup. A missing sanitizer
 * degrades the display of one field; it never opens the boundary.
 *
 * CommonJS on purpose, exactly like activeWorksheet.js, and `dompurify` is
 * required *lazily* inside the default resolution path only. A module-scope
 * require would break `node scripts/check-worksheet-helpers.js`, which loads
 * this file with plain Node on a host that has no node_modules; the checks
 * inject their own purifier through `options.purifier` instead.
 */

/**
 * Formatting tags only.
 *
 * No `a`, `img`, `iframe`, `table`, `style`, or `script`: nothing here can
 * load a subresource, navigate, or execute. Combined with an empty
 * `ALLOWED_ATTR` this also removes every event handler and every URL-bearing
 * attribute, so there is no `javascript:` surface left to filter.
 */
const WORKSHEET_HTML_ALLOWED_TAGS = [
  "b",
  "strong",
  "i",
  "em",
  "u",
  "s",
  "br",
  "p",
  "span",
  "div",
  "ul",
  "ol",
  "li",
  "sub",
  "sup",
  "small"
];

/**
 * The sanitizer policy.
 *
 * `KEEP_CONTENT: true` is load-bearing twice over: DOMPurify adds `#text` to
 * an explicit `ALLOWED_TAGS` set only when it is on, and it is what keeps the
 * text of a disallowed element (a link's label, a table cell's value) visible
 * instead of silently deleting guidance along with the tag.
 *
 * The three `RETURN_*` flags are pinned rather than left to the library
 * defaults so the return value is always a plain string that `v-html` can
 * take, whatever a future DOMPurify release defaults them to.
 */
const WORKSHEET_HTML_POLICY = {
  ALLOWED_TAGS: WORKSHEET_HTML_ALLOWED_TAGS,
  ALLOWED_ATTR: [],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  KEEP_CONTENT: true,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_TRUSTED_TYPE: false
};

/**
 * HTML-escape a value for text display.
 *
 * Also the fail-closed return of sanitizeWorksheetHtml(), which is why it
 * escapes quotes as well as angle brackets: the result is safe in an
 * attribute context too, not only between tags.
 *
 * @param {*} value
 * @returns {string} "" for a non-string or blank value
 */
function escapeWorksheetHtml(value) {
  if (typeof value !== "string" || value.trim() === "") {
    return "";
  }

  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * A purifier is usable only if it declares support *and* can sanitize.
 *
 * Both halves matter. Verified against dompurify 2.5.9: in a windowless
 * environment the package's export is a factory function carrying
 * `isSupported === false` and no `sanitize` at all, and DOMPurify's
 * `sanitize()` returns the dirty string unchanged whenever `isSupported` is
 * false. Checking only for a callable `sanitize` would therefore hand raw
 * markup straight to `v-html`.
 */
function isUsablePurifier(candidate) {
  return Boolean(
    candidate &&
      candidate.isSupported === true &&
      typeof candidate.sanitize === "function"
  );
}

/**
 * The window-ish object DOMPurify's factory needs.
 *
 * `window` in the browser and Electron renderer. `globalThis` is the fallback
 * only so an unusual host can still be offered something; DOMPurify itself
 * decides whether the object is a real DOM (it requires
 * `document.nodeType === 9`) and stays unsupported when it is not.
 */
function defaultWorksheetWindow() {
  if (typeof window !== "undefined" && window) {
    return window;
  }
  if (typeof globalThis !== "undefined" && globalThis) {
    return globalThis;
  }
  return null;
}

/**
 * Resolve a module export into a usable purifier, or null.
 *
 * Exported as a seam so the resolution itself is checkable without the real
 * package: pass a loader that returns any export shape.
 *
 * Order matters, and follows what dompurify actually exports:
 *
 * 1. A ready purifier (the browser case — the package builds one at import
 *    time when a DOM is present).
 * 2. A callable export (the windowless case, and webpack's ESM-interop
 *    namespace unwrapped below): call it with the window and re-check.
 * 3. Anything else: null, which makes callers escape instead of trust.
 *
 * Never throws: a loader that fails is just an unresolved purifier.
 *
 * @param {Function|object} loader factory returning the module export, or the
 *        export itself
 * @param {object} [win] window-ish object for the factory form
 * @returns {object|null}
 */
function resolveWorksheetPurifier(loader, win) {
  let exported = null;

  try {
    exported = typeof loader === "function" ? loader() : loader;
  } catch (error) {
    return null;
  }

  if (!exported) {
    return null;
  }

  // Interop unwrap. This module is CommonJS, but webpack 4 resolves
  // dompurify through its "module" field, so `require()` here can hand back
  // an ES-module namespace whose `default` is the real export rather than the
  // export itself. Without this the browser build would fail closed and
  // silently escape every worksheet note.
  if (
    !isUsablePurifier(exported) &&
    typeof exported !== "function" &&
    exported.default
  ) {
    exported = exported.default;
  }

  if (isUsablePurifier(exported)) {
    return exported;
  }

  if (typeof exported === "function") {
    const scope = win || defaultWorksheetWindow();
    if (!scope) {
      return null;
    }

    let created = null;
    try {
      created = exported(scope);
    } catch (error) {
      return null;
    }

    if (isUsablePurifier(created)) {
      return created;
    }
  }

  return null;
}

function loadDompurify() {
  // Lazy require: keeps plain-Node loads of this module working.
  return require("dompurify");
}

// Resolved once. `additional_info` is sanitized per row per render, so the
// require and the DOM capability probe must not repeat on every keystroke.
// The flag is separate from the value because null (no purifier) is a real
// resolved outcome that must also be remembered.
let cachedPurifier = null;
let cachedPurifierResolved = false;

function defaultWorksheetPurifier() {
  if (!cachedPurifierResolved) {
    cachedPurifierResolved = true;
    cachedPurifier = resolveWorksheetPurifier(loadDompurify);
  }
  return cachedPurifier;
}

/**
 * Sanitize worksheet HTML for `v-html`.
 *
 * Total: never throws, always returns a string. An unresolved purifier, an
 * unusable injected purifier, a throwing `sanitize()`, and a non-string
 * return all fall back to escapeWorksheetHtml() — the escaped source is
 * displayable and inert, whereas the raw source is neither.
 *
 * @param {*} value
 * @param {{purifier?: object}} [options] `purifier` injects a purifier and
 *        bypasses the module-level cache
 * @returns {string}
 */
function sanitizeWorksheetHtml(value, options) {
  if (typeof value !== "string" || value.trim() === "") {
    return "";
  }

  const settings = options || {};
  const injected = settings.purifier;
  const purifier =
    injected === null || injected === undefined
      ? defaultWorksheetPurifier()
      : injected;

  if (!isUsablePurifier(purifier)) {
    return escapeWorksheetHtml(value);
  }

  let clean;
  try {
    // A shallow copy, because DOMPurify freezes the configuration object it
    // is handed and keeps a reference to it. Passing the exported constant
    // would freeze this module's policy and let one library version's
    // behavior outlive the call.
    clean = purifier.sanitize(value, Object.assign({}, WORKSHEET_HTML_POLICY));
  } catch (error) {
    return escapeWorksheetHtml(value);
  }

  return typeof clean === "string" ? clean : escapeWorksheetHtml(value);
}

module.exports = {
  WORKSHEET_HTML_ALLOWED_TAGS: WORKSHEET_HTML_ALLOWED_TAGS,
  WORKSHEET_HTML_POLICY: WORKSHEET_HTML_POLICY,
  escapeWorksheetHtml: escapeWorksheetHtml,
  resolveWorksheetPurifier: resolveWorksheetPurifier,
  sanitizeWorksheetHtml: sanitizeWorksheetHtml
};
